import { MINER_DATA_TYPE } from "@components/Pages/Machines/MinerRow/MinerIdField/MinerDataCopy/MinerDataCopyControl";
import { IReproxyLinkEvent, ReproxyLinkRemoveEventTag } from "@components/Pages/Machines/MinerRow/minerReducer";
import { IMinerMeta } from "@services/changeMinerSettings";
import {
  IReproxyLink,
} from "@services/getMinerList";
import useApiRequests from "@services/http/useApiRequest";
import useMinerPorts from "@services/miner/MinerPorts.hook";
import { useAuth } from "@utils/hooks/useAuth";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import { makeNotifs } from "@utils/makeNotifs";
import { parseMinerName } from "@utils/parseMinerName";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IMinerNameProps } from "./MinerName.props";
import { FullMinerInfoResponse, GetFullMinerInfo } from "@services/getMinerInfo";

type MINER_DATA_WITHADDRESS_TYPE = MINER_DATA_TYPE | "ADDR";

export const useMinerNameControl = (
  props: Omit<IMinerNameProps, "className" | "fullAddres">
) => {
  const auth = useAuth();
  const { onSuccess } = useGlobalNotifs();
  const { ok } = makeNotifs();
  const { enqueue } = useApiRequests();
  const [showCopy, setShowCopy] = useState(false);
  const [loader, setLoader] = useState<
    undefined | MINER_DATA_WITHADDRESS_TYPE | "ALL"
  >();
  const [minerData, setMinerData] = useState<IMinerMeta>();
  const [reproxyLink, setReproxyLink] = useState<IReproxyLink>();

  useEffect(() => {
    setReproxyLink(props.reproxyLink);
  }, [props.reproxyLink]);

  const onShowCopy = (show: boolean) => setShowCopy(show);

  const name = useMemo(() => {
    const { sectionCoords, letter } = parseMinerName(props.children);

    if (props.useFullName || !sectionCoords) return props.children || "";

    return `${letter}${sectionCoords}`;
  }, [props.children]);

  const getMinerData = useCallback(async () => {
    if (!props.minerId) return;
    const res = await enqueue<FullMinerInfoResponse>(
      new GetFullMinerInfo(props.minerId.toString(), auth.isAdmin),
      {
        onLoadingStatusChanged(isLoading) {
          if (isLoading) {
            setLoader("ALL");
          } else {
            setLoader(undefined);
          }
        },
      }
    );
    if (!res) return;
    setMinerData(res.Record.meta);
    setReproxyLink(res.Record.reproxy_link);
    return res.Record.meta;
  }, [props.minerId]);

  const onLoadMinerData = async (b: boolean) => {
    if (!b) {
      props.setOptimize?.(true);
      return;
    }
    props.setOptimize?.(false);
    if (minerData) return;
    const data = await getMinerData();
    setMinerData(data);
  };

  const _onCopy = (text: string) => {
    if (navigator.clipboard?.writeText !== undefined) {
      navigator.clipboard.writeText(text);
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  const onCopyData = async (d: MINER_DATA_WITHADDRESS_TYPE) => {
    setLoader(d);

    switch (d) {
      case "ADDR":
        _onCopy(props.fullAddress || props.children);
        break;
      case "IP":
        _onCopy(minerData?.ip || "");
        break;
      case "MAC":
        _onCopy(minerData?.mac || "");
        break;

      case "PASS":
        _onCopy(minerData?.password || "");
        break;

      default:
        break;
    }
    setTimeout(() => {
      setLoader(undefined);
      onSuccess(ok.success);
    }, 500);
  };

  const updateRackReproxyLink = (port: number) => {
    setReproxyLink({
      Port: port,
      Target: "",
      LastActive: ""
    } as IReproxyLink)
  };

  const minerPorts = useMinerPorts({
    onPortChanged: updateRackReproxyLink
  });

  useEffect(() => {
    document.addEventListener(ReproxyLinkRemoveEventTag, onRemoveReproxyLink);
    return () => document.removeEventListener(ReproxyLinkRemoveEventTag, onRemoveReproxyLink);
    function onRemoveReproxyLink(event: Event) {
      const e = (event as CustomEvent).detail as IReproxyLinkEvent;
      if (props.minerId === e.minerId) {
        setReproxyLink(undefined)
      }
    }
  }, [props.minerId]);

  return {
    isCrop: name.length >= 8,
    isChangeFontSize: name.length > 4 && name.length < 7,
    showCopy,
    name,
    loader,
    minerData,
    reproxyLink,
    onShowCopy,
    onLoadMinerData,
    onCopyData,
    createPortAndOpen: minerPorts.createPortAndOpen,
    deleteMinerPort: minerPorts.deleteMinerPort,
  };
};
